import * as Sentry from '@sentry/remix';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import type {
  DynamicProductBlock as DynamicProductBlockType,
  RegularProductBlock as RegularProductBlockType
} from 'types/capi/content';
import type {
  Affiliate as AffiliateType,
  Image as ImageType,
  Price as PriceType,
  Shipping as ShippingType
} from 'types/capi/shared';

import VerifiedSellerIcon from '~/assets/icons/VerifiedSellerIcon';
import AffiliateProductLink from '~/components/AffiliateProductLink';
import * as Button from '~/components/Button';
import Image from '~/components/Image';
import ProductNotificationButton from '~/components/ProductNotificationButton';
import { getStockStatus, textColours } from '~/domain/stockStatus';
import AffiliateLinkClickEvent from '~/events/AffiliateLinkClickEvent';
import useEnv from '~/hooks/useEnv';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import wct from '~/services/wct.client';
import { formatPrice } from '~/utils/prices';
import { getShippingType } from '~/utils/shipping';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

import Links from '../Links';
import AffiliateSizes from './AffiliateSizes';

const DynamicProductBlock = ({ dpb }: { dpb: DynamicProductBlockType }) => {
  return (
    <div className="relative flex w-full flex-col items-center p-0">
      <ProductBlockImage image={dpb.image} />
      <Header header={dpb.header} />
      <div className="flex w-full justify-center px-2">
        <div className="flex w-full max-w-lg flex-col items-center gap-5 rounded-lg border border-concrete px-4 py-2">
          {dpb.cta.type === 'product_reminder' && (
            <div className="flex flex-col items-center">
              <StockStatus status={dpb.stock_status} />
              <div className="text-light-grey">{`${
                dpb.stock_status === 'SOLD_OUT'
                  ? 'Get notified when this product is back in stock'
                  : 'Get notified when this product is dropping'
              }`}</div>
            </div>
          )}

          {['external_product_link', 'internal'].includes(dpb.cta.type) && (
            <ProductInfo dpb={dpb} />
          )}
          <DynamicProductBlockCta dpb={dpb} />

          {dpb.other_affiliates_link && (
            <Links.Internal link={dpb.other_affiliates_link} />
          )}
        </div>
      </div>
    </div>
  );
};

const ProductInfo = ({ dpb }: { dpb: DynamicProductBlockType }) => {
  return (
    <div className="w-full">
      <div className="mb-2 flex items-center justify-between ">
        <AffiliateLogo affiliate={dpb.affiliate} />
        <StockStatus status={dpb.stock_status} />
        <div className="flex flex-col items-end leading-tight">
          <Price price={dpb.price} />
          <Shipping type={dpb.shipping} />
        </div>
      </div>
      {dpb.cta.type === 'external_product_link' && (
        <AffiliateSizes
          pagePosition="Dynamic Product Block"
          sizes={dpb.sizes}
          affiliate={dpb.affiliate}
          position={-1}
          price={dpb.price}
          product={{
            name: dpb.product_name,
            price: dpb.price,
            product_id: dpb.product_id
          }}
          urlTemplates={dpb.cta}
        />
      )}
    </div>
  );
};

const DynamicProductBlockCta = ({ dpb }: { dpb: DynamicProductBlockType }) => {
  switch (dpb.cta.type) {
    case 'external_product_link':
      return (
        <AffiliateProductLink
          affiliate={dpb.affiliate}
          pagePosition="Dynamic Product Block"
          buttonPosition={-1}
          price={dpb.price}
          product={{
            name: dpb.product_name,
            price: dpb.price,
            product_id: dpb.product_id
          }}
          urlTemplates={dpb.cta}
          variant="forest"
        >
          {dpb.cta.text}
        </AffiliateProductLink>
      );
    case 'internal': {
      return (
        <Button.InternalLink to={dpb.cta.path} variant="forest">
          {dpb.cta.text}
        </Button.InternalLink>
      );
    }
    case 'product_reminder': {
      return (
        <ProductNotificationButton
          product={{
            affiliateName: dpb.affiliate.name,
            id: dpb.product_id,
            image: dpb.image,
            legacyId: dpb.cta.legacy_id,
            name: dpb.product_name,
            price: dpb.price,
            stockStatus: getStockStatus(dpb.stock_status)
          }}
          className="h-12 w-full"
          pagePosition="Dynamic Product Block"
          placement="content_block"
          buttonStyle="normal"
        />
      );
    }
    default:
      return null;
  }
};

const RegularProductBlock = ({ rpb }: { rpb: RegularProductBlockType }) => {
  const user = useUser();
  const env = useEnv();
  const marketingCookies = useMarketingCookies();
  const { clientBrowser, clientIp, isBot, session } = useRootData();
  const { pathname, search } = useLocation();

  const { url, clickRef } = getTrackedUrlAndClickRef(rpb.cta, { user });

  const discountCode = rpb.cta.discount_code?.code || '';

  function fireTracking() {
    wct.fireEvent({
      clickRef,
      clientBrowser,
      clientIp,
      landingPage: marketingCookies.getLandingPage() ?? 'unknown',
      pagePath: `${env.SITE_BASE_URL}${pathname}${search}`,
      session,
      url
    });

    if (rpb.cta.affiliate)
      new AffiliateLinkClickEvent({
        affiliateName: rpb.cta.affiliate.name,
        clientIp,
        isBot,
        isReseller: rpb.cta.affiliate.is_reseller,
        pagePosition: 'Featured Product Block',
        retailerClickPrice:
          rpb.cta.price.sale ?? rpb.cta.price.regular ?? undefined,
        retailerId: rpb.cta.affiliate.id,
        user
      }).send();
  }

  async function copyDiscountCodeToClipboard() {
    await navigator.clipboard.writeText(discountCode);
  }

  async function onCtaButtonClick(e: React.MouseEvent<HTMLSpanElement>) {
    fireTracking();

    try {
      if (!discountCode) return;
      e.preventDefault();

      await copyDiscountCodeToClipboard();
      window.open(url);
    } catch (error) {
      Sentry.captureMessage(`Failed to copy text to clipboard: ${error}`);
    }
  }

  return (
    <div className="relative flex w-full flex-col items-center p-0">
      <ProductBlockImage image={rpb.image} />
      <Header header={rpb.header} />
      <div className="mx-auto flex w-full justify-center px-2">
        <div className="flex w-full max-w-md flex-col items-center gap-3 rounded-lg border border-concrete p-4">
          <div className="flex w-full items-center justify-between">
            <AffiliateLogo affiliate={rpb.cta.affiliate} />
            {rpb.cta.discount_code && (
              <button
                type="button"
                className="flex-1 cursor-pointer text-center text-sm text-purple-hola"
                onClick={copyDiscountCodeToClipboard}
              >
                Click here {rpb.cta.discount_code?.text}
              </button>
            )}
            {rpb.cta.affiliate?.id === 'affiliate:marketplace' && (
              <VerifiedSellerBadge />
            )}
            <div className="flex flex-col items-end leading-tight">
              {rpb.cta.affiliate?.id === 'affiliate:marketplace' ? (
                <>
                  <span className="text-grey-read">Prices from</span>
                  <Price price={rpb.cta.price} />
                  <span className="text-grey-hide">UK Shipping</span>
                </>
              ) : (
                <Price price={rpb.cta.price} />
              )}
              <Shipping type={rpb.cta.shipping} />
            </div>
          </div>
          <Button.ExternalLink
            onClick={onCtaButtonClick}
            rel="sponsored nofollow noopener"
            target="_blank"
            variant="forest"
            href={url}
          >
            {rpb.cta.discount_code
              ? 'Copy Code & Buy Now'
              : rpb.cta.button_text}
          </Button.ExternalLink>
        </div>
      </div>
    </div>
  );
};

function VerifiedSellerBadge() {
  return (
    <div className="flex items-center gap-2">
      <VerifiedSellerIcon />
      <p className="font-semibold">Verified Sellers</p>
    </div>
  );
}

const Header = ({ header }: { header: string }) => {
  return (
    <h3 className="mx-0 mb-4 mt-6 max-w-2xl bg-white p-0 px-3 text-lg font-semibold leading-tight lg:px-24 lg:pt-1">
      {header}
    </h3>
  );
};

const ProductBlockImage = ({ image }: { image?: ImageType }) => {
  if (!image) return null;

  return (
    <Image
      caption={image.caption}
      loading="lazy"
      src={image.url}
      alt={image.alt}
      responsiveOptions={{
        lg: { height: 672, width: 1440 },
        md: { height: 450, width: 1024 },
        sm: { height: 450, width: 800 },
        xs: { height: 300, width: 672 }
      }}
    />
  );
};

const AffiliateLogo = ({ affiliate }: { affiliate?: AffiliateType }) => {
  if (!affiliate) return null;

  return (
    <div className="w-20">
      <Image
        src={affiliate.logo}
        loading="lazy"
        alt={`${affiliate.name}-logo`}
        responsiveOptions={{
          xs: { width: 150 }
        }}
      />
    </div>
  );
};

const Price = ({ price }: { price?: PriceType }) => {
  const { t } = useTranslation();

  if (price?.regular && price?.sale)
    return (
      <div className="flex flex-1 flex-col items-end text-sm leading-none">
        <div className="line-through">
          {formatPrice(price.regular, price.currency)}
        </div>
        <div className="font-bold text-red-alert">
          {formatPrice(price.sale, price.currency)}
        </div>
      </div>
    );

  if (price?.regular)
    return <div>{formatPrice(price.regular, price.currency)}</div>;

  return (
    <div>
      {t('productInfoPrice')} {t('tbcLabel')}
    </div>
  );
};

const Shipping = ({ type }: { type?: ShippingType }) => {
  return <span className="text-caption-grey">{getShippingType({ type })}</span>;
};

const StockStatus = ({ status }: { status: string }) => {
  const { t } = useTranslation();
  const stockStatus = getStockStatus(status);

  if (!stockStatus) return null;

  return (
    <div className={`text-lg font-semibold ${textColours[stockStatus]}`}>
      {t(stockStatus)}
    </div>
  );
};

export default {
  Dynamic: DynamicProductBlock,
  Regular: RegularProductBlock
};
